import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import isUndefined from 'lodash/isUndefined';
import RXService from '@Services/RxService';
import HeatmapHelperService from '@Pages/Heatmap/HeatmapHelperService';
import { HEATMAP_OBSERVABLE_TOPICS } from '@Pages/Heatmap/constants';
import { ResizeMatrixSubject } from '@Pages/Heatmap/HeatmapObservableSubjects';

export const MIN_SCROLLBAR_SIZE = 15;

const setElementScroll = (el, { scrollTop, scrollLeft }) => {
  if (!isUndefined(scrollTop)) {
    // eslint-disable-next-line no-param-reassign
    el.scrollTop = scrollTop;
  }

  if (!isUndefined(scrollLeft)) {
    // eslint-disable-next-line no-param-reassign
    el.scrollLeft = scrollLeft;
  }
};

// eslint-disable-next-line react/display-name
const Scrollable = forwardRef(({ onScroll, containerHeight, containerWidth, children }, selfRef) => {
  const _drag = useRef(false);
  const _startPosition = useRef({});
  const _lastPosition = useRef({});
  const _scrollType = useRef();

  const [verticalScrollOffset, setVerticalScrollOffset] = useState(0);
  const [horizontalScrollOffset, setHorizontalScrollOffset] = useState(0);
  const [maxHScrollPos, setMaxHScrollPos] = useState(0);
  const [maxVScrollPos, setMaxVScrollPos] = useState(0);
  const [hScrollLength, setHScrollLength] = useState(0);
  const [vScrollLength, setVScrollLength] = useState(0);
  const [vScrollStartPos, setVScrollStartPos] = useState(0);
  const [hScrollStartPos, setHScrollStartPos] = useState(0);
  const [maxScrollLeft, setMaxScrollLeft] = useState(0);
  const [maxScrollTop, setMaxScrollTop] = useState(0);

  const setupScrollParams = useCallback(() => {
    const { top: _top, left: _left } = selfRef.current.getBoundingClientRect();
    const {
      clientHeight: _clientHeight,
      clientWidth: _clientWidth,
      scrollWidth: _scrollWidth,
      scrollHeight: _scrollHeight,
    } = selfRef.current;
    const scrollWidth = _scrollWidth;
    const scrollHeight = _scrollHeight;

    let hScrollSize = (_clientWidth * _clientWidth) / scrollWidth;
    if (hScrollSize < MIN_SCROLLBAR_SIZE) {
      hScrollSize = MIN_SCROLLBAR_SIZE;
    }

    let vScrollSize = (_clientHeight * _clientHeight) / scrollHeight;
    if (vScrollSize < MIN_SCROLLBAR_SIZE) {
      vScrollSize = MIN_SCROLLBAR_SIZE;
    }

    setMaxHScrollPos(_clientWidth - hScrollSize);
    setMaxVScrollPos(_clientHeight - vScrollSize);
    setHScrollLength(hScrollSize);
    setVScrollLength(vScrollSize);
    setHScrollStartPos(_left);
    setVScrollStartPos(_top);
    setMaxScrollLeft(_scrollWidth - _clientWidth);
    setMaxScrollTop(_scrollHeight - _clientHeight);

    ResizeMatrixSubject.next({
      viewportWidth: _clientWidth > scrollWidth ? scrollWidth : _clientWidth,
      viewportHeight: _clientHeight > scrollHeight ? scrollHeight : _clientHeight,
      totalWidth: scrollWidth,
      totalHeight: scrollHeight,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerHeight, containerWidth]);

  const scrollHandler = useCallback(() => {
    const { scrollTop, scrollLeft } = selfRef.current;

    setVerticalScrollOffset((scrollTop / maxScrollTop) * maxVScrollPos);
    setHorizontalScrollOffset((scrollLeft / maxScrollLeft) * maxHScrollPos);

    onScroll(scrollTop, scrollLeft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxHScrollPos, maxScrollLeft, maxScrollTop, maxVScrollPos, onScroll]);

  const disableDrag = useCallback(() => {
    _drag.current = false;
  }, []);

  const onHorizontalSliderDrag = (e) => {
    const mouseMoveXDistance = _startPosition.current.x - e.clientX;
    let newHorizontalScrollOffset = horizontalScrollOffset - mouseMoveXDistance + _lastPosition.current.x;

    if (newHorizontalScrollOffset < 0) {
      newHorizontalScrollOffset = 0;
    }

    if (newHorizontalScrollOffset > maxHScrollPos) {
      newHorizontalScrollOffset = maxHScrollPos;
    }

    setHorizontalScrollOffset(newHorizontalScrollOffset);

    _lastPosition.current.x = mouseMoveXDistance;
    setElementScroll(selfRef.current, { scrollLeft: (newHorizontalScrollOffset / maxHScrollPos) * maxScrollLeft });
  };

  const onVerticalSliderDrag = (e) => {
    const mouseMoveYDistance = _startPosition.current.y - e.clientY;
    // compensation of prev scrollings and applying just last movement: start - last
    let newVerticalScrollOffset = verticalScrollOffset - mouseMoveYDistance + _lastPosition.current.y;

    if (newVerticalScrollOffset < 0) {
      newVerticalScrollOffset = 0;
    }

    if (newVerticalScrollOffset > maxVScrollPos) {
      newVerticalScrollOffset = maxVScrollPos;
    }

    setVerticalScrollOffset(newVerticalScrollOffset);

    _lastPosition.current.y = mouseMoveYDistance;
    setElementScroll(selfRef.current, { scrollTop: (newVerticalScrollOffset / maxVScrollPos) * maxScrollTop });
  };

  const mouseMoveHandler = (e) => {
    if (_drag.current) {
      switch (_scrollType.current) {
        case HeatmapHelperService.scrollType.horizontal:
          onHorizontalSliderDrag(e);
          break;
        case HeatmapHelperService.scrollType.vertical:
          onVerticalSliderDrag(e);
          break;
        default:
      }
    }
  };

  const sliderTouched = (e) => {
    e.preventDefault();

    _drag.current = true;
    _startPosition.current = {
      x: e.clientX,
      y: e.clientY,
    };
    _lastPosition.current = {
      x: 0,
      y: 0,
    };
  };

  const verticalScrollSliderTouched = (e) => {
    sliderTouched(e);
    _scrollType.current = HeatmapHelperService.scrollType.vertical;
  };

  const horizontalScrollSliderTouched = (e) => {
    sliderTouched(e);
    _scrollType.current = HeatmapHelperService.scrollType.horizontal;
  };

  useEffect(() => {
    const rxSubscriptions = [
      RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.resizeWindow, () => {
        setupScrollParams();
        scrollHandler();
      }),

      RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.scrollWindow, () => {
        setupScrollParams();
        scrollHandler();
      }),

      RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.resizeHeader, () => {
        setupScrollParams();
        scrollHandler();
      }),
    ];

    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [setupScrollParams, scrollHandler]);

  useEffect(() => {
    const rxSubscriptions = [
      RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.movedNavigator, (scrollLeft, scrollTop) => {
        setElementScroll(selfRef.current, { scrollTop, scrollLeft });
      }),
    ];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setupScrollParams();
    window.addEventListener('mouseup', disableDrag, false);

    return () => {
      window.removeEventListener('mouseup', disableDrag, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { scrollHeight, scrollWidth, clientHeight, clientWidth } = selfRef.current || {};

  return (
    <div className="heatmap__scrollable" onScroll={scrollHandler} onMouseMove={mouseMoveHandler} ref={selfRef}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="heatmap__scrollable__handle heatmap__scrollable__handle--vertical"
        style={{
          top: `${verticalScrollOffset + vScrollStartPos}px`,
          height: `${vScrollLength}px`,
          display: scrollHeight > clientHeight ? 'block' : 'none',
        }}
        onMouseDown={verticalScrollSliderTouched}
      />
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="heatmap__scrollable__handle heatmap__scrollable__handle--horizontal"
        style={{
          left: `${horizontalScrollOffset + hScrollStartPos}px`,
          width: `${hScrollLength}px`,
          display: scrollWidth > clientWidth ? 'block' : 'none',
        }}
        onMouseDown={horizontalScrollSliderTouched}
      />
      <div>{children}</div>
    </div>
  );
});

Scrollable.propTypes = {
  onScroll: PropTypes.func,
  containerHeight: PropTypes.number,
  containerWidth: PropTypes.number,
  children: PropTypes.node,
};

export default Scrollable;
